import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import MarketingReveal from './MarketingReveal';

/**
 * Shared PeerSpace-style chrome for public marketing pages.
 * Media/dark heroes, split intros, open grids — elevated, not sparse card stacks.
 */

export const MarketingPage = ({ children, className = '' }) => (
  <div className={`flex min-h-screen flex-col bg-white ${className}`.trim()}>{children}</div>
);

/** Compact white text intro (legal / utility pages). */
export const MarketingPageHero = ({
  eyebrow = null,
  title,
  subtitle = null,
  actions = null,
  align = 'center',
}) => {
  const alignClass = align === 'left' ? 'text-left' : 'text-center';
  const wrapClass = 'mx-auto max-w-3xl';

  return (
    <section className="border-b border-m-border-light bg-white px-4 py-14 sm:px-6 sm:py-20">
      <MarketingReveal className={`${wrapClass} ${alignClass}`}>
        {eyebrow ? (
          <p className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-m-ink-faint">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="marketing-hero-title mb-4" style={{ color: '#0a0a0a' }}>
          {title}
        </h1>
        {subtitle ? (
          <div
            className={`mb-8 max-w-xl text-[1.05rem] leading-relaxed ${
              align === 'center' ? 'mx-auto' : ''
            }`}
            style={{ color: '#6b6b6b' }}
          >
            {subtitle}
          </div>
        ) : null}
        {actions ? (
          <div
            className={`flex flex-col gap-3 sm:flex-row sm:items-center ${
              align === 'center' ? 'items-stretch justify-center sm:justify-center' : 'items-start'
            }`}
          >
            {actions}
          </div>
        ) : null}
      </MarketingReveal>
    </section>
  );
};

/**
 * PeerSpace-style media or charcoal hero.
 * image → full-bleed photo + overlay; otherwise solid dark band.
 */
export const MarketingMediaHero = ({
  title,
  subtitle = null,
  image = null,
  actions = null,
  height = 'md',
}) => {
  const heights = {
    sm: 'min-h-[280px] sm:min-h-[320px]',
    md: 'min-h-[340px] sm:min-h-[420px]',
    lg: 'min-h-[400px] sm:min-h-[520px]',
  };

  return (
    <section
      className={`relative flex items-center justify-center overflow-hidden px-4 py-16 sm:px-6 sm:py-20 ${
        heights[height] || heights.md
      }`}
      style={!image ? { backgroundColor: '#1a1a1a' } : undefined}
    >
      {image ? (
        <>
          <img
            src={image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            aria-hidden
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.65) 100%)',
            }}
          />
        </>
      ) : null}

      <MarketingReveal className="relative z-10 mx-auto max-w-3xl text-center">
        <h1
          className="marketing-hero-title mb-4"
          style={{ color: '#ffffff' }}
        >
          {title}
        </h1>
        {subtitle ? (
          <p className="mx-auto mb-8 max-w-xl text-[1.05rem] leading-relaxed text-white/75">
            {subtitle}
          </p>
        ) : null}
        {actions ? (
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            {actions}
          </div>
        ) : null}
      </MarketingReveal>
    </section>
  );
};

/** Left bold title + right body — PeerSpace intro pattern. */
export const MarketingSplitIntro = ({ title, children, className = '' }) => (
  <MarketingReveal
    className={`mx-auto grid max-w-6xl gap-8 md:grid-cols-12 md:gap-12 ${className}`.trim()}
  >
    <div className="md:col-span-5">
      <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
        {title}
      </h2>
    </div>
    <div
      className="space-y-4 text-[1.05rem] leading-relaxed md:col-span-7"
      style={{ color: '#6b6b6b' }}
    >
      {children}
    </div>
  </MarketingReveal>
);

/** Icon + label rows with hairline dividers (no cards). */
export const MarketingFeatureGrid = ({ items = [], className = '' }) => (
  <div
    className={`mx-auto grid max-w-6xl grid-cols-1 border-t border-m-border-light sm:grid-cols-2 ${className}`.trim()}
  >
    {items.map((item, index) => {
      const Icon = item.icon;
      const inner = (
        <>
          {Icon ? <Icon className="mt-0.5 h-5 w-5 shrink-0 text-m-ink" strokeWidth={1.5} /> : null}
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold tracking-tight" style={{ color: '#0a0a0a' }}>
              {item.title}
            </p>
            {item.description ? (
              <p className="mt-1 text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
                {item.description}
              </p>
            ) : null}
          </div>
          {item.href || item.to ? (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-m-ink-faint" />
          ) : null}
        </>
      );

      const rowClass =
        'flex items-start gap-4 border-b border-m-border-light px-1 py-6 transition-colors hover:bg-m-canvas-light/60 sm:px-3';

      if (item.to) {
        return (
          <Link key={item.title || index} to={item.to} className={rowClass}>
            {inner}
          </Link>
        );
      }
      if (item.href) {
        return (
          <a key={item.title || index} href={item.href} className={rowClass}>
            {inner}
          </a>
        );
      }
      return (
        <div key={item.title || index} className={rowClass}>
          {inner}
        </div>
      );
    })}
  </div>
);

/** Two image+copy columns — PeerSpace story blocks. */
export const MarketingImagePair = ({ items = [] }) => (
  <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 md:grid-cols-2 md:gap-8">
    {items.map((item) => (
      <MarketingReveal key={item.title}>
        {item.image ? (
          <div className="mb-5 aspect-[16/10] overflow-hidden bg-m-canvas-light">
            <img
              src={item.image}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : null}
        <h3 className="mb-2 text-xl font-bold tracking-tight sm:text-2xl" style={{ color: '#0a0a0a' }}>
          {item.title}
        </h3>
        <p className="text-[15px] leading-relaxed" style={{ color: '#6b6b6b' }}>
          {item.description}
        </p>
        {item.linkLabel && (item.to || item.href) ? (
          <div className="mt-4">
            {item.to ? (
              <Link
                to={item.to}
                className="inline-flex items-center gap-1.5 text-sm font-medium underline-offset-2 hover:underline"
                style={{ color: '#0a0a0a' }}
              >
                {item.linkLabel}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : (
              <a
                href={item.href}
                className="inline-flex items-center gap-1.5 text-sm font-medium underline-offset-2 hover:underline"
                style={{ color: '#0a0a0a' }}
              >
                {item.linkLabel}
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        ) : null}
      </MarketingReveal>
    ))}
  </div>
);

export const MarketingSection = ({
  children,
  tone = 'white',
  className = '',
  id,
}) => {
  const tones = {
    white: 'bg-white',
    canvas: 'bg-m-canvas-light',
    charcoal: '',
    night: 'bg-m-night text-white',
  };

  return (
    <section
      id={id}
      className={`px-4 py-16 sm:px-6 sm:py-24 ${tones[tone] || tones.white} ${className}`.trim()}
      style={tone === 'charcoal' ? { backgroundColor: '#3a3a3a' } : undefined}
    >
      {children}
    </section>
  );
};

export const MarketingSectionHeader = ({
  eyebrow = null,
  title,
  subtitle = null,
  light = false,
  className = '',
}) => (
  <MarketingReveal className={`mb-10 max-w-3xl sm:mb-12 ${className}`.trim()}>
    {eyebrow ? (
      <p
        className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em]"
        style={{ color: light ? 'rgba(255,255,255,0.55)' : '#8a8a8a' }}
      >
        {eyebrow}
      </p>
    ) : null}
    <h2
      className="marketing-section-title mb-3"
      style={{ color: light ? '#ffffff' : '#0a0a0a' }}
    >
      {title}
    </h2>
    {subtitle ? (
      <p
        className="max-w-xl text-[1.05rem] leading-relaxed"
        style={{ color: light ? 'rgba(255,255,255,0.72)' : '#6b6b6b' }}
      >
        {subtitle}
      </p>
    ) : null}
  </MarketingReveal>
);

/** Soft surface for forms only — avoid for static content. */
export const MarketingPanel = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl bg-white px-6 py-7 sm:px-7 sm:py-8 ${className}`.trim()}
    style={{ color: '#0a0a0a' }}
  >
    {children}
  </div>
);

export const MarketingPrimaryButton = ({ to, href, children, className = '' }) => {
  const classes = `marketing-btn marketing-btn-ink inline-flex gap-2 ${className}`.trim();
  if (href) {
    return (
      <a href={href} className={classes}>
        {children}
        <ArrowRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link to={to || '/'} className={classes}>
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
};

export const MarketingSecondaryButton = ({ to, href, children, className = '' }) => {
  const classes = `inline-flex h-[var(--m-control-h)] items-center justify-center gap-2 rounded-full border border-m-border-light px-6 text-sm font-medium text-m-ink transition-colors hover:border-m-ink ${className}`.trim();
  if (href) {
    return (
      <a href={href} className={classes}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to || '/'} className={classes}>
      {children}
    </Link>
  );
};

export const MarketingGhostOnDark = ({ to, href, children, className = '' }) => {
  const classes = `marketing-btn marketing-btn-ghost inline-flex gap-2 ${className}`.trim();
  if (href) {
    return (
      <a href={href} className={classes}>
        {children}
        <ArrowRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link to={to || '/'} className={classes}>
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
};

/** White pill CTA for use on photo/dark heroes. */
export const MarketingHeroLightButton = ({ to, href, children, className = '' }) => {
  const classes = `marketing-btn inline-flex gap-2 !bg-white !text-m-ink hover:!bg-white/90 ${className}`.trim();
  if (href) {
    return (
      <a href={href} className={classes}>
        {children}
        <ArrowRight className="h-4 w-4" />
      </a>
    );
  }
  return (
    <Link to={to || '/'} className={classes}>
      {children}
      <ArrowRight className="h-4 w-4" />
    </Link>
  );
};
